import { ChangeDetectorRef, Component, ElementRef, inject, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { of } from 'rxjs';
import { catchError, defaultIfEmpty, take } from 'rxjs/operators';
import { DashboardBulkSelectionService } from '../../../services/dashboard-bulk-selection.service';
import { IConfigSlider } from '../../../classes/custom-range-slider';
import { AuthenticationService } from '../../../services/authentication.service';
import { DashboardSearchBase } from '../common/dashboard-search-base';
import {
  inferTaxPlay, isActiveTaxFilterTag, TAX_COMMON_FINANCIAL_SLIDERS, TAX_EPC_SLIDERS,
  TAX_FILTER_LABELS, TAX_PAF_FILTER_KEYS, TAX_PLAY_FINANCIAL_SLIDERS, TAX_PLAYS,
  TaxFilterTag, TaxPlayId, taxWorkspaceFilterKeys, unsupportedTaxQueryKeys,
} from './tax-accountancy-dashboard.config';
import { taxMagicCategory, TaxMagicCategory } from './tax-magic-relevance';
import { taxFilterHelpText as getTaxFilterHelpText } from './tax-filter-help';

const TAX_NEWS_SOURCE_VALUES = new Set(['external', 'websiteNews', 'selfPublished', 'either']);

interface TaxMagicFilterGroup {
  questionId: number;
  title: string;
  category: TaxMagicCategory;
  filters: Record<string, any>[];
  unavailableFilters: Record<string, any>[];
}

interface TaxMagicModule {
  id: number;
  name: string;
  hasFilterAccess: boolean;
  groups: TaxMagicFilterGroup[];
}

interface TaxMagicFilterIdentity {
  moduleId: number;
  questionId: number;
  fieldName: string;
  filterType: string;
  label: string;
  available: boolean;
}

type TaxResultView = 'overview' | 'researchAndDevelopment' | 'capitalAllowances' | 'energy' | 'international';

interface TaxResultInsight {
  questionId: number;
  fieldKey: string;
  label: string;
  value: unknown;
  dateGenerated: string | null;
  confidence: number | null;
  source: string;
  estimated: boolean;
}

const TAX_RESULT_VIEWS: ReadonlyArray<{ id: TaxResultView; label: string; icon: string; description: string }> = [
  { id: 'overview', label: 'Overview', icon: 'fa-grid-2', description: 'Company size, status and recorded advisers' },
  { id: 'researchAndDevelopment', label: 'R&D', icon: 'fa-flask', description: 'Stored research and innovation signals' },
  { id: 'capitalAllowances', label: 'Capital allowances', icon: 'fa-building', description: 'Stored property and asset relief signals' },
  { id: 'energy', label: 'Energy', icon: 'fa-bolt', description: 'Stored energy and decarbonisation signals' },
  { id: 'international', label: 'International', icon: 'fa-globe', description: 'Stored cross-border signals and trade context' },
];

/** Focused search surface over the full dashboard's existing filters. */
@Component({
  selector: 'app-tax-accountancy-dashboard',
  templateUrl: './tax-accountancy-dashboard.component.html',
  styleUrls: ['./tax-accountancy-dashboard.component.css'],
  standalone: false,
  providers: [DashboardBulkSelectionService],
})
export class TaxAccountancyDashboardComponent extends DashboardSearchBase {
  @ViewChild('taxSearchPane') private taxSearchPane?: ElementRef<HTMLElement>;
  private readonly taxRoute = inject(ActivatedRoute);
  private readonly taxRouter = inject(Router);
  private readonly taxCdr = inject(ChangeDetectorRef);
  private readonly taxAuthenticationService = inject(AuthenticationService);
  readonly taxPlays = TAX_PLAYS;
  readonly taxResultViews = TAX_RESULT_VIEWS;
  taxResultView: TaxResultView = 'overview';

  get taxResultViewDetails(): typeof TAX_RESULT_VIEWS[number] {
    return this.taxResultViews.find(view => view.id === this.taxResultView) || this.taxResultViews[0];
  }
  readonly taxFilterLabels = TAX_FILTER_LABELS;
  readonly taxFilterHelpText = getTaxFilterHelpText;
  readonly taxCommonFinancialSliders = TAX_COMMON_FINANCIAL_SLIDERS;
  readonly taxEpcSliders = TAX_EPC_SLIDERS;
  readonly taxAllFinancialSliders = Object.values(TAX_PLAY_FINANCIAL_SLIDERS).flat()
    .filter((definition, index, all) =>
      all.findIndex(candidate => candidate.key === definition.key) === index);
  taxFinancialMeasureQuery = '';
  taxCompanyStatusQuery = '';
  taxCompanyCategoryQuery = '';
  taxUkRegionQuery = '';
  taxSicMode: 'include' | 'exclude' = 'include';
  taxPostcodeMode: 'prefix' | 'district' | 'radius' = 'prefix';
  taxMagicModules: TaxMagicModule[] = [];
  private taxMagicFilterKeys = new Map<string, TaxMagicFilterIdentity>();
  activeTaxPlay: TaxPlayId = 'rd';
  taxHasSearched = false;
  taxActiveOnly = true;
  taxDefaultState: 'idle' | 'saving' | 'saved' | 'error' = 'idle';
  taxSaveOpen = false;
  private taxAppliedFilterSignature = '';
  private taxCriteriaRefreshTimer?: ReturnType<typeof setTimeout>;
  private taxSafeInitialQueryParams?: Record<string, any>;

  override ngOnInit(): void {
    const queryParams = this.taxRoute.snapshot.queryParams;
    const pafKeys = Object.keys(queryParams).filter(key => TAX_PAF_FILTER_KEYS.has(key));
    if (pafKeys.length) {
      // PAF access is loaded asynchronously. Resolve it before restoring a
      // shared search, so an unlicensed land/EPC criterion cannot auto-submit.
      this.taxAuthenticationService.getPlanFeaturesEnabled().pipe(
        take(1),
        defaultIfEmpty({ has_paf_access: false }),
        catchError(() => of({ has_paf_access: false })),
      ).subscribe((features: any) => {
        const hasPAFAccess = features?.has_paf_access === true;
        const safeParams = { ...queryParams };
        if (!hasPAFAccess) {
          pafKeys.forEach(key => delete safeParams[key]);
          this.taxSafeInitialQueryParams = safeParams;
        }
        if (this.initialiseTaxFromQueryParams(safeParams, hasPAFAccess) && !hasPAFAccess) {
          this.taxRouter.navigate(['/dashboard/tax-accountancy'], {
            queryParams: safeParams, replaceUrl: true,
          });
        }
      });
      return;
    }
    this.initialiseTaxFromQueryParams(queryParams, false);
  }

  private initialiseTaxFromQueryParams(
    queryParams: Record<string, any>, hasPAFAccess: boolean
  ): boolean {
    const requestedPlay = queryParams['play'];
    const validPlay = this.taxPlays.some(play => play.id === requestedPlay);
    const inferredPlay = inferTaxPlay(queryParams);
    if (requestedPlay && !validPlay) {
      this.openUnsupportedSearchInFullDashboard(queryParams);
      return false;
    }
    this.activeTaxPlay = validPlay ? requestedPlay as TaxPlayId : inferredPlay || 'rd';
    if (unsupportedTaxQueryKeys(queryParams, this.activeTaxPlay, hasPAFAccess)
      .some(key => !this.isPotentialTaxMagicQuery(key, queryParams[key])) ||
        String(queryParams['postcodeBeginsWith'] || '').includes(',') ||
        !this.validTaxStatusQuery(queryParams['selectedCompanyStatuses']) ||
        !this.validTaxNewsSourceQuery(queryParams['newsSource']) ||
        String(queryParams['upperDaysUntilFinancialYearEnd'] || '') === '0') {
      this.openUnsupportedSearchInFullDashboard(queryParams);
      return false;
    }
    if (hasPAFAccess) this.hasPAFAccess = true;
    super.ngOnInit();
    if (hasPAFAccess) this.hasPAFAccess = true;
    return true;
  }

  override initialiseBasedUponGetParamsIfAllLoaded(): void {
    // Strip presentation keys before the base interprets URL keys as search
    // filters. In particular, bare ?play=energy must stay an empty workspace.
    const { play, resultsMode, pageNumber, ...searchParams } =
      this.taxSafeInitialQueryParams ?? this.urlQueryParams;
    this.taxSafeInitialQueryParams = undefined;
    if (Object.entries(searchParams).some(([param, value]) =>
      this.isPotentialTaxMagicQuery(param, value) &&
      !this.isActiveTaxMagicTag({ param, value }) &&
      this.isNonemptyTaxMagicValue(value))) {
      this.openUnsupportedSearchInFullDashboard(searchParams);
      return;
    }
    const activeParams = Object.fromEntries(Object.entries(searchParams)
      .filter(([param, value]) =>
        isActiveTaxFilterTag({ param, value }) ||
        this.isActiveTaxMagicTag({ param, value })));
    if (activeParams['newsSource'] === 'either') {
      // The full dashboard's legacy "either" means the default Any source.
      delete activeParams['newsSource'];
    }
    if (!activeParams['lowerRevenue'] && !activeParams['upperRevenue']) {
      delete activeParams['ignoreRevenueEstimates'];
    }
    const hasCriterion = Object.keys(activeParams).some(param =>
      param !== 'selectedCompanyStatuses' &&
      (param !== 'selectedTerritory' || !this.areTerritoriesEnforced)
    );
    this.urlQueryParams = hasCriterion ? activeParams : {};
    if (!activeParams['selectedCompanyStatuses']) this.selectActiveCompaniesInUI();
    super.initialiseBasedUponGetParamsIfAllLoaded();
    this.taxPostcodeMode = this.postcodeWithinRadius ? 'radius'
      : this.postcodeDistrict ? 'district' : 'prefix';
    this.taxActiveOnly = (this.companyStatusOptions || []).some(option =>
      option.checked && option.status_clean === 'Active Companies') &&
      (this.companyStatusOptions || []).filter(option => option.checked).length === 1;
    this.refreshSearchTagsFromCurrentFilters();
  }

  private validTaxStatusQuery(value: unknown): boolean {
    if (value === undefined || value === null) return true;
    try {
      const selected = JSON.parse(String(value));
      return Array.isArray(selected) && selected.length > 0 &&
        selected.every(status => typeof status === 'string' && status.length < 80);
    } catch (_) {
      return false;
    }
  }

  private validTaxNewsSourceQuery(value: unknown): boolean {
    return value === undefined || value === null || value === '' ||
      (typeof value === 'string' && TAX_NEWS_SOURCE_VALUES.has(value));
  }

  private openUnsupportedSearchInFullDashboard(params: Record<string, any>): void {
    const { play, resultsMode, ...fullQueryParams } = params;
    this.taxRouter.navigate(['/dashboard'], { queryParams: fullQueryParams });
  }

  get taxSearchLoading(): boolean {
    return this.isLoading().loadingCompanies || this.isLoading().loadingCompanyCount;
  }

  get taxPostcodeValid(): boolean {
    const singlePostcode = [this.postcodeBeginsWith, this.postcodeDistrict, this.postcodeWithinRadius]
      .every(value => !String(value || '').includes(','));
    return singlePostcode && (!this.postcodeWithinRadius ||
      Number(this.mileRadiusOfPostcode) > 0);
  }

  get taxYearEndValid(): boolean {
    const lower = this.lowerDaysUntilFinancialYearEnd;
    const upper = this.upperDaysUntilFinancialYearEnd;
    const valid = (value: number | null) => value === null || value === undefined ||
      (Number.isFinite(Number(value)) && Number(value) >= 0 && Number(value) <= 365);
    return valid(lower) && valid(upper) &&
      (upper === null || upper === undefined || Number(upper) >= 1) &&
      (lower === null || upper === null || lower === undefined || upper === undefined ||
        Number(lower) <= Number(upper));
  }

  get taxNewsDaysValid(): boolean {
    const value = this.newsWithinDays;
    if (value === null || value === undefined || String(value).trim() === '') return true;
    return Number.isFinite(Number(value)) && Number(value) >= 1;
  }

  get taxActiveTags(): TaxFilterTag[] {
    const visible = taxWorkspaceFilterKeys();
    return (this.searchTags || []).filter(tag =>
      ((visible.has(tag.param) && isActiveTaxFilterTag(tag)) ||
        this.isActiveTaxMagicTag(tag)) &&
      (tag.param !== 'newsSource' ||
        (this.validTaxNewsSourceQuery(tag.value) && tag.value !== 'either')) &&
      (tag.param !== 'ignoreRevenueEstimates' || this.taxHasRevenueRange)
    );
  }

  get taxHasRevenueRange(): boolean {
    const range = this.sliderValues?.['Revenue'];
    return range?.lower !== null && range?.lower !== undefined ||
      range?.upper !== null && range?.upper !== undefined;
  }

  get taxFilterCount(): number {
    return this.taxActiveTags.filter(tag => tag.param !== 'selectedCompanyStatuses').length;
  }

  get taxHasSearchCriterion(): boolean {
    return this.taxActiveTags.some(tag =>
      tag.param !== 'selectedCompanyStatuses' &&
      tag.param !== 'ignoreRevenueEstimates' &&
      (tag.param !== 'selectedTerritory' || !this.areTerritoriesEnforced)
    );
  }

  get taxHasPendingChanges(): boolean {
    return this.taxHasSearched &&
      this.taxFilterSignature() !== this.taxAppliedFilterSignature;
  }

  private taxFilterSignature(): string {
    return JSON.stringify(this.taxActiveTags
      .map(tag => ({ param: tag.param, value: String(tag.value) }))
      .sort((a, b) => a.param.localeCompare(b.param)));
  }

  /** The config endpoint is the source of truth for entitled question fields. */
  override updateAiraFilterDetails(data: Record<string, any>[]): void {
    // The focused workspace exposes the shared Tax template only. Private
    // organisation modules remain available in the Full dashboard. Filter
    // before the base builds its search state so a private module cannot be
    // retained invisibly through a saved or shared Tax URL.
    const publicTaxModules = (data || []).filter(module =>
      module.sectionName === 'Magic Insights - Tax' && module.isOrganisationModule !== true);
    // The base stores values by label. Use the question and field identity so
    // two same-labelled Tax fields cannot overwrite each other. Remove
    // non-prospecting fields before the base can build searchable controls.
    const modules: Record<string, any>[] = publicTaxModules.map(module => ({
      ...module,
      questions: (module.questions || []).map(question => ({
        ...question,
        filters: Object.fromEntries(Object.entries(question.filters || {})
          .map(([groupKey, filters]: [string, any[]]) => [groupKey,
            filters.filter(filter => filter.fieldName !== 'email_opener' &&
              !/^(strategic_goals|key_risks|challenges)__/.test(filter.fieldName || ''))
              .map(filter => ({
                ...filter,
                taxDisplayLabel: filter.label,
                label: `MI Q${question.id} ${filter.fieldName}`,
              })),
          ])),
      })).filter(question => Object.values(question.filters)
        .some((filters: any[]) => filters.length > 0)),
    }));
    super.updateAiraFilterDetails(modules);
    this.taxMagicFilterKeys.clear();
    this.taxMagicModules = modules.map(module => {
      const groups = (module.questions || []).map(question => {
        const filters = Object.values(question.filters || {}).flat() as Record<string, any>[];
        const availableFilters = filters.filter(filter => this.taxMagicUnavailableReason(filter) === '');
        const unavailableFilters = filters.filter(filter => this.taxMagicUnavailableReason(filter) !== '');
        filters.forEach(filter => {
          const identity: TaxMagicFilterIdentity = {
            moduleId: module.id,
            questionId: question.id,
            fieldName: filter.fieldName,
            filterType: filter.filterType,
            label: filter.taxDisplayLabel,
            available: Boolean(module.hasFilterAccess) && this.taxMagicUnavailableReason(filter) === '',
          };
          for (const key of filter.filterType === 'range_slider'
            ? [`lower${filter.label}`, `upper${filter.label}`] : [filter.label]) {
            this.taxMagicFilterKeys.set(key, identity);
          }
        });
        return {
          questionId: question.id,
          title: this.taxMagicQuestionTitle(module.sectionName, question.id, filters),
          category: taxMagicCategory(module.sectionName, question.id, filters),
          filters: availableFilters,
          unavailableFilters,
        };
      }).filter(group => group.filters.length || group.unavailableFilters.length);
      return {
        id: module.id,
        name: module.sectionName,
        hasFilterAccess: Boolean(module.hasFilterAccess),
        groups,
      };
    }).filter(module => module.groups.length);
  }

  taxMagicModulesForCategory(category: TaxMagicCategory): TaxMagicModule[] {
    return this.taxMagicModules
      .map(module => ({
        ...module,
        groups: module.groups.filter(group => group.category === category),
      }))
      .filter(module => module.groups.length > 0);
  }

  taxMagicQuestionCount(category: TaxMagicCategory): number {
    return this.taxMagicModulesForCategory(category)
      .reduce((count, module) => count + module.groups.length, 0);
  }

  taxVisibleTeamTags(options: any[]): any[] {
    const selected = options.filter(tag => tag.checked);
    const matches = options.filter(tag => !tag.checked && tag.shouldShow !== false);
    return [...selected, ...matches.slice(0, 30)];
  }

  override filterTags(): void {
    const query = this.tagSearchTerm.trim().toLocaleLowerCase();
    for (const tag of [...this.tagsPositiveOptions, ...this.tagsNegativeOptions]) {
      tag.shouldShow = !query || String(tag.name || '').toLocaleLowerCase().includes(query);
    }
  }

  taxMagicQuestionTitle(moduleName: string, id: number, filters: Record<string, any>[]): string {
    // The local demo and production can assign different question IDs. The
    // public Tax schema's field names are the stable identity for headings.
    const fieldTitles: Array<[RegExp, string]> = [
      [/^tax_arrangement_summary$/, 'Tax arrangements'],
      [/^(auditors|accountants|tax_advisors)__/, 'Recorded advisers'],
      [/^r_and_d_tax_relief_/, 'R&D relief estimate'],
      [/^capital_allowances_tax_relief_/, 'Capital allowances estimate'],
      [/^land_remediation_tax_relief_/, 'Land remediation estimate'],
      [/^eii_ccl_scheme_/, 'Energy scheme estimate'],
      [/^patent_box_tax_relief_/, 'Patent Box estimate'],
      [/^innovate_uk_grant_funding_/, 'Innovate UK funding estimate'],
      [/^creative_industries_tax_relief_/, 'Creative industries estimate'],
    ];
    if (moduleName === 'Magic Insights - Tax') {
      const titles = new Set(filters.map(filter =>
        fieldTitles.find(([pattern]) => pattern.test(filter.fieldName))?.[1]
      ).filter((title): title is string => Boolean(title)));
      if (titles.size === 1) return [...titles][0];
    }
    const standardTitles: Record<number, string> = {
      235: 'Tax arrangements', 237: 'Recorded advisers',
      239: 'R&D relief estimate',
      240: 'Capital allowances estimate', 241: 'Land remediation estimate',
      242: 'Energy scheme estimate', 243: 'Patent Box estimate',
      244: 'Innovate UK funding estimate', 245: 'Creative industries estimate',
    };
    if (moduleName === 'Magic Insights - Tax' && standardTitles[id]) return standardTitles[id];
    const distinctive = filters.find(filter =>
      filter.taxDisplayLabel && !/^reasoning contains$/i.test(filter.taxDisplayLabel));
    return distinctive?.taxDisplayLabel || `Research question ${id}`;
  }

  taxMagicUnavailableReason(filter: Record<string, any>): string {
    if (Number(filter.airaQuestionId) === 1166 &&
        filter.fieldName === 'revenue_growth_percentages') {
      return 'This array-valued answer cannot be searched as a numeric range yet.';
    }
    if (!['range_slider', 'text_input', 'multi_select'].includes(filter.filterType)) {
      return 'This filter type is not available in search.';
    }
    return '';
  }

  private isPotentialTaxMagicQuery(param: string, value: unknown): boolean {
    if (param === 'selectedModules') return false;
    try {
      const field = JSON.parse(String(value));
      return Number.isInteger(Number(field.airaQuestionId)) &&
        typeof field.fieldName === 'string' && !!field.fieldName &&
        ['range_slider', 'text_input', 'multi_select'].includes(field.filterType) &&
        (field.filterType === 'range_slider'
          ? (param.startsWith('lower') || param.startsWith('upper'))
          : !param.startsWith('lower') && !param.startsWith('upper'));
    } catch (_) { return false; }
  }

  private isNonemptyTaxMagicValue(value: unknown): boolean {
    try {
      const parsed = JSON.parse(String(value));
      return parsed.value !== null && parsed.value !== undefined &&
        parsed.value !== '' && (!Array.isArray(parsed.value) || parsed.value.length > 0);
    } catch (_) { return false; }
  }

  private isActiveTaxMagicTag(tag: TaxFilterTag): boolean {
    if (!this.isPotentialTaxMagicQuery(tag.param, tag.value) ||
        !this.isNonemptyTaxMagicValue(tag.value)) return false;
    const identity = this.taxMagicFilterKeys.get(tag.param);
    if (!identity?.available) return false;
    const value = JSON.parse(String(tag.value));
    return Number(value.airaQuestionId) === identity.questionId &&
      value.fieldName === identity.fieldName && value.filterType === identity.filterType;
  }

  taxFilterLabel(tag: TaxFilterTag): string {
    const magic = this.taxMagicFilterKeys.get(tag.param);
    return magic ? `${magic.label} · ${this.taxMagicModules.find(module => module.id === magic.moduleId)?.name || 'Magic Insights'}`
      : this.taxFilterLabels[tag.param] || tag.param;
  }

  updateTaxMagicSlider(values: Record<string, any>, filter: Record<string, any>): void {
    this.triggerConfigSliderWithObjectUpdate(values, filter);
    this.queueTaxCriteriaRefresh();
  }

  updateTaxMagicText(value: string, filter: Record<string, any>): void {
    this.triggerConfigTextInputWithObjectUpdate(value, filter);
    this.queueTaxCriteriaRefresh();
  }

  updateTaxMagicOptions(filter: Record<string, any>): void {
    this.triggerConfigMultiSelectInputWithObjectUpdate(filter);
    this.queueTaxCriteriaRefresh();
  }

  updateTaxMagicConfidence(values: Record<string, any>, filter: Record<string, any>): void {
    this.triggerConfidenceSliderUpdate(values, filter);
    this.queueTaxCriteriaRefresh();
  }

  queueTaxCriteriaRefresh(): void {
    clearTimeout(this.taxCriteriaRefreshTimer);
    this.taxCriteriaRefreshTimer = setTimeout(() => {
      this.normalizeTaxDependencies();
      this.refreshSearchTagsFromCurrentFilters();
      // This timer can run outside Angular's change-detection turn. Update the
      // button binding now so a newly entered criterion enables the search.
      this.taxCdr.detectChanges();
    }, 50);
  }

  private normalizeTaxDependencies(): void {
    if (!this.validTaxNewsSourceQuery(this.newsSource) || this.newsSource === 'either') {
      this.newsSource = '';
    }
    if (!this.taxHasRevenueRange) this.ignoreRevenueEstimates = false;
  }

  runTaxSearch(): void {
    if (!this.taxPostcodeValid || !this.taxYearEndValid || !this.taxNewsDaysValid || this.taxSearchLoading ||
        this.isLoading().loadingUI || !this.allEndpointsLoaded() || this.isReadOnlyUser) return;
    this.normalizeTaxDependencies();
    this.refreshSearchTagsFromCurrentFilters();
    if (!this.taxHasSearchCriterion) return;
    // An open filter group uses the full canvas for editing. Return to the
    // compact two-pane layout when the user asks to see the search results.
    this.taxSearchPane?.nativeElement.querySelectorAll<HTMLDetailsElement>('.tax-dashboard__group[open]')
      .forEach(group => { group.open = false; });
    this.searchCompaniesAfterFilters(true);
  }

  override searchCompaniesAfterFilters(initialisePageCounter: boolean): void {
    this.normalizeTaxDependencies();
    this.refreshSearchTagsFromCurrentFilters();
    this.taxHasSearched = true;
    super.searchCompaniesAfterFilters(initialisePageCounter);
    this.taxAppliedFilterSignature = this.taxFilterSignature();
    this.syncTaxRouteFromCurrentFilters();
  }

  selectTaxPlay(play: TaxPlayId): void {
    if (play === this.activeTaxPlay) return;
    // Service selection changes profile context only. Every grouped filter
    // remains visible and selected, including cross-service combinations.
    this.activeTaxPlay = play;
    this.syncTaxRouteFromCurrentFilters();
    this.taxCdr.markForCheck();
  }

  private clearTaxArraySelection(key: string): boolean {
    const optionsByKey: Record<string, any[]> = {
      selectedFinancialYearEnds: this.financialYearEndsOptions,
      selectedOfficeCountries: this.countriesOfOfficesOptions,
      selectedGeographicalRevenueCountries: this.countriesOfGeographicalRevenueOptions,
      selectedGeographicalRevenueTerritories: this.territoriesOfGeographicalRevenueOptions,
      selectedImportExportCategories: this.importExportCategories,
    };
    if (!Object.prototype.hasOwnProperty.call(optionsByKey, key)) return false;
    this[key] = [];
    (optionsByKey[key] || []).forEach(option => option.checked = false);
    return true;
  }

  resetTaxFilters(preserveContext = false): void {
    const name = preserveContext ? this.companyNameContains : '';
    const territory = preserveContext || this.areTerritoriesEnforced
      ? this.selectedTerritory : null;
    this.resetAllFiltersAndUpdateFilters();
    this.companyNameContains = name;
    this.selectedTerritory = territory;
    this.postcodeBeginsWith = '';
    this.accountsSearchTerm = '';
    this.newsContaining = '';
    this.newsWithinDays = null;
    this.newsSource = '';
    this.homepageSearchTerm = '';
    this.hiringJobTitleContains = '';
    this.recentDirectorshipTitleContains = '';
    this.accountantNameContains = '';
    this.auditorNameContains = '';
    this.bankerNameContains = '';
    this.selectedAccountants = [];
    this.selectedAuditors = [];
    this.selectedBankers = [];
    this.bankerSearchInput = '';
    this.possibleBankers = [];
    this.noOutstandingCharges = false;
    this.chargeRegistered = false;
    this.outstandingChargeRegistered = false;
    this.chargeHolderNameContains = '';
    this.ignoreSatisfiedCharges = false;
    this.chargeCreatedMonthsAgoLower = null;
    this.chargeCreatedMonthsAgoUpper = null;
    this.selectedLatestAccountsAuditOption = '';
    this.accountsAreOverdue = false;
    this.hasAnyPatentRecord = false;
    this.hasRaisedInvestment = false;
    this.issuedAGrant = false;
    this.hasLandTitle = false;
    this.ignoreRevenueEstimates = false;
    this.excludeCompaniesTaggedByTeam = false;
    this.taxActiveOnly = true;
    Object.keys({
      selectedFinancialYearEnds: true,
      selectedOfficeCountries: true,
      selectedGeographicalRevenueCountries: true,
      selectedGeographicalRevenueTerritories: true,
      selectedImportExportCategories: true,
    }).forEach(key => this.clearTaxArraySelection(key));
    this.selectActiveCompaniesInUI();
    this.taxHasSearched = false;
    this.taxAppliedFilterSignature = '';
    this.companies = [];
    this.pageNumber = 1;
    this.sicCodeTextSearch = '';
    this.taxFinancialMeasureQuery = '';
    this.taxUkRegionQuery = '';
    this.taxSicMode = 'include';
    this.taxPostcodeMode = 'prefix';
    this.refreshSearchTagsFromCurrentFilters();
    this.syncTaxRouteFromCurrentFilters();
    this.taxCdr.markForCheck();
  }

  private syncTaxRouteFromCurrentFilters(): void {
    const queryParams: Record<string, unknown> = { play: this.activeTaxPlay };
    this.taxActiveTags.forEach(tag => queryParams[tag.param] = tag.value);
    // Mirroring the selected service and filters into the URL is not a page
    // change, so opt out of the router's scroll-to-top and keep the reader's place.
    void this.taxRouter.navigate(['/dashboard/tax-accountancy'], {
      queryParams,
      replaceUrl: true,
      scroll: 'manual',
    });
  }

  setTaxActiveOnly(value: boolean): void {
    this.taxActiveOnly = value;
    (this.companyStatusOptions || []).forEach(option => {
      option.checked = value && option.status_clean === 'Active Companies';
    });
    this.queueTaxCriteriaRefresh();
  }

  setTaxStatusOption(option: Record<string, any>, selected: boolean): void {
    option.checked = selected;
    this.taxActiveOnly = (this.companyStatusOptions || []).some(status =>
      status.checked && status.status_clean === 'Active Companies') &&
      (this.companyStatusOptions || []).filter(status => status.checked).length === 1;
    this.queueTaxCriteriaRefresh();
  }

  get taxFilteredCompanyStatusOptions(): Array<any> {
    const query = this.taxCompanyStatusQuery.trim().toLocaleLowerCase();
    if (!query) return this.companyStatusOptions || [];
    return (this.companyStatusOptions || []).filter(option =>
      String(option.status_clean || '').toLocaleLowerCase().includes(query));
  }

  get taxFilteredCompanyCategoryOptions(): Array<any> {
    const query = this.taxCompanyCategoryQuery.trim().toLocaleLowerCase();
    if (!query) return this.companyCategoryOptions || [];
    return (this.companyCategoryOptions || []).filter(option =>
      String(option.name || '').toLocaleLowerCase().includes(query));
  }

  get taxFilteredUkRegions(): Array<any> {
    const query = this.taxUkRegionQuery.trim().toLocaleLowerCase();
    if (!query) return this.ukRegions || [];
    return (this.ukRegions || []).filter(region =>
      String(region.name || '').toLocaleLowerCase().includes(query));
  }

  setTaxPostcodeMode(mode: 'prefix' | 'district' | 'radius'): void {
    this.taxPostcodeMode = mode;
    if (mode !== 'prefix') this.postcodeBeginsWith = '';
    if (mode !== 'district') this.postcodeDistrict = '';
    if (mode !== 'radius') {
      this.postcodeWithinRadius = '';
      this.mileRadiusOfPostcode = null;
    } else if (this.mileRadiusOfPostcode === null || this.mileRadiusOfPostcode === undefined) {
      this.mileRadiusOfPostcode = 10;
    }
    this.updateAddressTypeOnInputChange();
    this.queueTaxCriteriaRefresh();
  }

  setTaxRadiusMiles(event: Event): void {
    this.mileRadiusOfPostcode = Number((event.target as HTMLInputElement).value);
    this.queueTaxCriteriaRefresh();
  }

  normalizeTaxRadiusMiles(): void {
    const value = Number(this.mileRadiusOfPostcode);
    this.mileRadiusOfPostcode = Number.isFinite(value) && value > 0
      ? Math.min(500, Math.max(1, Math.round(value))) : 10;
    this.queueTaxCriteriaRefresh();
  }

  updateTaxChargeSelection(changed: 'noOutstandingCharges' | 'outstandingChargeRegistered'): void {
    if (changed === 'noOutstandingCharges' && this.noOutstandingCharges) {
      this.outstandingChargeRegistered = false;
    } else if (changed === 'outstandingChargeRegistered' && this.outstandingChargeRegistered) {
      this.noOutstandingCharges = false;
    }
    this.queueTaxCriteriaRefresh();
  }

  get taxExcludedSics(): Record<string, any>[] {
    return (this.companySICCodeOptions || []).filter(option => option.excluded);
  }

  get taxSicSuggestions(): Record<string, any>[] {
    if ((this.sicCodeTextSearch || '').trim().length < 3) return [];
    return (this.companySICCodeOptions || [])
      .filter(option => option.shouldShow && option.identifier)
      .slice(0, 12);
  }

  get taxSelectedSics(): Record<string, any>[] {
    return (this.companySICCodeOptions || []).filter(option => option.checked);
  }

  setTaxSic(option: Record<string, any>, selected: boolean): void {
    if (this.taxSicMode === 'include') {
      option.checked = selected;
      if (selected) option.excluded = false;
    } else {
      option.excluded = selected;
      if (selected) option.checked = false;
    }
    this.queueTaxCriteriaRefresh();
  }

  removeTaxSic(option: Record<string, any>, mode: 'include' | 'exclude'): void {
    option[mode === 'include' ? 'checked' : 'excluded'] = false;
    this.queueTaxCriteriaRefresh();
  }

  taxSlider(label: string): IConfigSlider | undefined {
    return Object.values(this.zintSlidersConfigs || {})
      .flat()
      .find((slider: IConfigSlider) => slider.label === label);
  }

  taxFinancialSliderAvailable(label: string): boolean {
    if (['Title Price Paid', 'Ownership Age Months'].includes(label)) return this.hasPAFAccess;
    if (label.startsWith('Esos')) return this.hasEnergyAndEmissionsAccess;
    return true;
  }

  matchesTaxFinancialMeasure(definition: { key: string; label: string }): boolean {
    const query = this.taxFinancialMeasureQuery.trim().toLowerCase();
    return !query || definition.label.toLowerCase().includes(query) ||
      definition.key.toLowerCase().includes(query);
  }

  get taxFinancialMeasureCount(): number {
    return this.taxAllFinancialSliders.filter(definition =>
      this.taxFinancialSliderAvailable(definition.key) && !!this.taxSlider(definition.key)).length;
  }

  get taxMatchingFinancialMeasureCount(): number {
    return this.taxAllFinancialSliders.filter(definition =>
      this.taxFinancialSliderAvailable(definition.key) && !!this.taxSlider(definition.key) &&
      this.matchesTaxFinancialMeasure(definition)).length;
  }

  updateTaxSlider(event: Record<string, any>, label: string): void {
    this.triggerConfigSliderUpdate(event, label);
    this.queueTaxCriteriaRefresh();
  }

  clearTaxFilter(tag: TaxFilterTag): void {
    const param = tag.param;
    if (this.taxMagicFilterKeys.has(param)) {
      this.clearTaxMagicFilter(param);
    } else if (param === 'selectedCompanyStatuses') {
      this.setTaxActiveOnly(false);
    } else if (param === 'selectedSICCodes') {
      (this.companySICCodeOptions || []).forEach(option => option.checked = false);
    } else if (param === 'excludedSICCodes') {
      (this.companySICCodeOptions || []).forEach(option => option.excluded = false);
    } else if (param === 'selectedCompanyCategories') {
      (this.companyCategoryOptions || []).forEach(option => option.checked = false);
    } else if (this.clearTaxArraySelection(param)) {
      // Array-backed options must be unchecked before updateFilters rebuilds them.
    } else if (param === 'selectedAccountants' || param === 'selectedAuditors' || param === 'selectedBankers') {
      this[param] = [];
    } else if (param === 'tagPositiveIdsSelected' || param === 'tagNegativeIdsSelected') {
      const options = param === 'tagPositiveIdsSelected' ? this.tagsPositiveOptions : this.tagsNegativeOptions;
      (options || []).forEach(option => option.checked = false);
    } else if (param === 'selectedCustomColumns') {
      (this.customColumns || []).forEach(column => {
        (column.options || []).forEach(option => option.checked = false);
        column.dropdownInput = '';
        column.lockFilter = '';
        column.boxInputLower = null;
        column.boxInputUpper = null;
      });
    } else if (param === 'ukRegion') {
      (this.ukRegions || []).forEach(option => option.checked = false);
    } else if (param === 'selectedTerritory') {
      if (!this.areTerritoriesEnforced) this.selectedTerritory = null;
    } else if (param === 'lowerRevenue' || param === 'upperRevenue') {
      this.clearTaxSlider('Revenue');
    } else if (param.includes('LandAndBuildingsAdditionsTangibleAssets')) {
      this.clearTaxSlider('LandAndBuildingsAdditionsTangibleAssets');
    } else if (param.endsWith('Corporation Tax')) {
      this.clearTaxSlider('Corporation Tax');
    } else if ((param.startsWith('lower') || param.startsWith('upper')) &&
               this.taxSlider(param.slice(5))) {
      this.clearTaxSlider(param.slice(5));
    } else if (param === 'employeeLowerCount' || param === 'employeeUpperCount') {
      this.employeeLowerCount = null;
      this.employeeUpperCount = null;
      this.headcountSliderElement?.reset();
    } else if (param === 'lowerTotalEnergyKwhSelected' || param === 'upperTotalEnergyKwhSelected') {
      this.lowerTotalEnergyKwhSelected = null;
      this.upperTotalEnergyKwhSelected = null;
      this.totalEnergyKwhSliderElement?.reset();
    } else if (param === 'lowerGasKwhSelected' || param === 'upperGasKwhSelected') {
      this.lowerGasKwhSelected = null;
      this.upperGasKwhSelected = null;
      this.gasKwhSliderElement?.reset();
    } else if (param === 'lowerElectricKwhSelected' || param === 'upperElectricKwhSelected') {
      this.lowerElectricKwhSelected = null;
      this.upperElectricKwhSelected = null;
      this.electricKwhSliderElement?.reset();
    } else if (param === 'lowerPatentAwardedDays' || param === 'upperPatentAwardedDays') {
      this.lowerPatentAwardedDays = null;
      this.upperPatentAwardedDays = null;
      this.patentAwardedWithinDaysSliderElement?.reset();
    } else if (param === 'chargeCreatedMonthsAgoLower' || param === 'chargeCreatedMonthsAgoUpper') {
      this.chargeCreatedMonthsAgoLower = null;
      this.chargeCreatedMonthsAgoUpper = null;
      this.chargeCreatedSliderElement?.reset();
    } else if (typeof this[param] === 'boolean') {
      this[param] = false;
    } else if (typeof this[param] === 'number') {
      this[param] = null;
      // Keep the visual thumb in sync when a range endpoint is removed via a
      // filter chip. The search value remains null until the user moves it.
      const slider = this.keyToSliderElementMap?.[param];
      if (slider && /lower|upper/i.test(param)) {
        if (/upper/i.test(param)) {
          slider.updateSliderValues({ upperUpdate: slider.max });
        } else {
          slider.updateSliderValues({ lowerUpdate: slider.min });
        }
      }
    } else if (typeof this[param] === 'string') {
      this[param] = '';
    }
    this.normalizeTaxDependencies();
    this.refreshSearchTagsFromCurrentFilters();
    this.syncTaxRouteFromCurrentFilters();
    this.taxCdr.markForCheck();
  }

  private clearTaxMagicFilter(param: string): void {
    const identity = this.taxMagicFilterKeys.get(param);
    if (!identity) return;
    const label = identity.filterType === 'range_slider' ? param.slice(5) : param;
    if (identity.filterType === 'range_slider') {
      delete this.sliderValues[label];
      this.genericConfigSliders?.find(slider => slider.sliderConfig?.label === label)?.reset();
    } else if (identity.filterType === 'text_input') {
      delete this.configTextInputValues[label];
      this.taxMagicModules.flatMap(module => module.groups)
        .flatMap(group => group.filters)
        .filter(filter => filter.label === label)
        .forEach(filter => filter.value = '');
    } else {
      delete this.configMultiSelectValues[label];
      this.taxMagicModules.flatMap(module => module.groups)
        .flatMap(group => group.filters)
        .filter(filter => filter.label === label)
        .forEach(filter => (filter.options || []).forEach(option => option.checked = false));
    }
    const confidenceKey = `confidence:${label}Aira${identity.questionId}`;
    delete this.confidenceValues[confidenceKey];
    this.confidenceSliders?.find(slider => slider.formName === confidenceKey)?.reset();
  }

  private clearTaxSlider(label: string): void {
    delete this.sliderValues[label];
    this.genericConfigSliders?.find(slider =>
      slider.formName === label || slider.sliderConfig?.label === label)?.reset();
  }

  taxTagValue(tag: TaxFilterTag): string {
    const value = tag.value;
    if (this.taxMagicFilterKeys.has(tag.param)) {
      try {
        const selected = JSON.parse(String(value)).value;
        return Array.isArray(selected) ? selected.join(', ') : String(selected);
      } catch (_) { return ''; }
    }
    if (value === true || value === 'true') return '';
    if (tag.param === 'selectedTerritory') {
      if (value && typeof value === 'object') return String((value as any).name || '');
      try { return JSON.parse(String(value))?.name || ''; } catch (_) { return ''; }
    }
    if (typeof value === 'string' && value.startsWith('[')) {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          if (tag.param === 'selectedCustomColumns') return `${parsed.length} workspace ${parsed.length === 1 ? 'field' : 'fields'}`;
          if (tag.param === 'selectedAccountants' || tag.param === 'selectedAuditors' || tag.param === 'selectedBankers') {
            return parsed.map(supplier => supplier.canonical_name || supplier.name || supplier.id).join(', ');
          }
          if (tag.param === 'tagPositiveIdsSelected' || tag.param === 'tagNegativeIdsSelected') {
            return `${parsed.length} ${parsed.length === 1 ? 'tag' : 'tags'}`;
          }
          return parsed.join(', ');
        }
      } catch (_) { /* Fall through to the raw value. */ }
    }
    const text = String(value);
    return text.length > 50 ? text.slice(0, 47) + '…' : text;
  }

  taxCompanyNumber(company: Record<string, any>): string {
    return String(company['Number'] ?? company.number ?? '');
  }

  taxCompanyName(company: Record<string, any>): string {
    return String(company['Company Name'] || 'Company');
  }

  taxLogoSrc(company: Record<string, any>): string {
    const domain = this.getLogisticsWebsiteLabel(company);
    return domain && !company.taxLogoFailed
      ? `https://www.google.com/s2/favicons?sz=64&domain=${encodeURIComponent(domain)}`
      : '';
  }

  // Google answers an unknown domain with a 16px generic globe, so treat that
  // as no logo and show the company initial instead.
  onTaxLogoLoad(company: Record<string, any>, image: HTMLImageElement): void {
    if (image.naturalWidth > 0 && image.naturalWidth <= 16) company.taxLogoFailed = true;
  }

  taxDisplayValue(value: unknown): string {
    if (value === null || value === undefined || value === '') return 'Not available';
    return String(value);
  }

  taxRevenue(company: Record<string, any>): string {
    const raw = company['Revenue'];
    if (raw === null || raw === undefined || raw === '') return 'Not available';
    const numeric = typeof raw === 'number' ? raw : Number(String(raw).replace(/[,£]/g, ''));
    return Number.isFinite(numeric)
      ? new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(numeric)
      : String(raw);
  }

  taxRevenueEstimated(company: Record<string, any>): boolean {
    return company['Is Revenue Estimated?'] === true ||
      company['Is Revenue Estimated?'] === 'true';
  }

  taxResultInsights(company: Record<string, any>): TaxResultInsight[] {
    if (this.taxResultView === 'overview') return [];
    const insights = company['_taxAccountancyInsights']?.[this.taxResultView];
    return Array.isArray(insights) ? insights.filter(insight =>
      insight && insight.value !== null && insight.value !== undefined && insight.value !== '') : [];
  }

  taxResultContext(company: Record<string, any>): Array<{ label: string; value: string }> {
    if (this.taxResultView !== 'international') return [];
    return [
      { label: 'Imports', value: this.taxInsightValue(company['Basic Imports']) },
      { label: 'Exports', value: this.taxInsightValue(company['Basic Exports']) },
      { label: 'Trading countries', value: this.taxInsightValue(company['Countries of Trading Addresses']) },
    ].filter(item => item.value !== 'Not available');
  }

  taxInsightValue(value: unknown): string {
    if (value === null || value === undefined || value === '') return 'Not available';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number') return new Intl.NumberFormat('en-GB', { maximumFractionDigits: 2 }).format(value);
    if (typeof value === 'string') return value.trim() || 'Not available';
    if (Array.isArray(value)) {
      const parts = value.map(item => this.taxInsightValue(item)).filter(item => item !== 'Not available');
      return parts.length ? parts.join(' · ') : 'Not available';
    }
    if (typeof value === 'object') {
      const parts = Object.entries(value as Record<string, unknown>)
        .filter(([key]) => !['confidence', 'date_generated', 'source'].includes(key))
        .map(([key, item]) => {
          const display = this.taxInsightValue(item);
          return display === 'Not available' ? '' : `${key.replace(/_/g, ' ')}: ${display}`;
        }).filter(Boolean);
      return parts.length ? parts.join(' · ') : 'Not available';
    }
    return String(value);
  }

  openFullDashboard(): void {
    this.normalizeTaxDependencies();
    this.refreshSearchTagsFromCurrentFilters();
    const visible = taxWorkspaceFilterKeys();
    const saved = JSON.parse(this.getJSONParamsToSave(false));
    const queryParams: Record<string, any> = {};
    Object.keys(saved).forEach(key => {
      if (visible.has(key) || this.isActiveTaxMagicTag({ param: key, value: saved[key] })) {
        queryParams[key] = saved[key];
      }
    });
    this.taxRouter.navigate(['/dashboard'], {
      queryParams,
      info: { dashboardModeSwitch: true },
    });
  }

  makeTaxDefault(): void {
    if (this.taxDefaultState === 'saving') return;
    this.taxDefaultState = 'saving';
    this.taxAuthenticationService.setDefaultSkin('tax_accountancy').subscribe({
      next: () => {
        this.taxDefaultState = 'saved';
        this.taxCdr.markForCheck();
      },
      error: () => {
        this.taxDefaultState = 'error';
        this.taxCdr.markForCheck();
      },
    });
  }

  saveTaxSearch(): void {
    this.normalizeTaxDependencies();
    this.refreshSearchTagsFromCurrentFilters();
    if (this.taxHasSearchCriterion) super.saveSearch();
  }

  override updateAfterSearchHasBeenSaved(data: Record<string, any>): void {
    super.updateAfterSearchHasBeenSaved(data);
    if (data?.success) this.taxSaveOpen = false;
  }

  override ngOnDestroy(): void {
    clearTimeout(this.taxCriteriaRefreshTimer);
    super.ngOnDestroy();
  }
}
